import { Controller } from "@hotwired/stimulus";

const PAGE_SIZE = 20;
const POLL_MS = 1000;

export default class extends Controller {
  static targets = ["chip", "section"];

  initialize() {
    this.saved = {};
  }

  sectionTargetConnected(section) {
    this.arrange(section);
    this.restore(section);
  }

  sectionTargetDisconnected(section) {
    const open = Array.from(section.querySelectorAll("tr.parts-row--open")).map((row) => {
      const detail = this.detailOf(row);
      return { id: row.dataset.detail, html: detail.querySelector(".parts-detail__content").innerHTML, loaded: detail.dataset.loaded };
    });
    this.saved[section.dataset.section] = { page: Number(section.dataset.page || 0), open };
  }

  toggle(event) {
    const chip = event.currentTarget;
    chip.setAttribute("aria-pressed", chip.getAttribute("aria-pressed") !== "true");
    this.sectionTargets.forEach((section) => {
      this.arrange(section);
      this.paginate(section, 0);
    });
  }

  open(event) {
    const row = event.currentTarget;
    this.expand(row, row.querySelector("[aria-expanded]").getAttribute("aria-expanded") !== "true");
  }

  expand(row, expanded) {
    const detail = this.detailOf(row);
    row.querySelector("[aria-expanded]").setAttribute("aria-expanded", expanded);
    row.classList.toggle("parts-row--open", expanded);
    detail.hidden = !expanded;
    if (expanded && !detail.dataset.loaded) this.load(detail, 0);
  }

  async load(detail, waited) {
    if (!detail.isConnected) return;

    const content = detail.querySelector(".parts-detail__content");
    const url = new URL(detail.dataset.url, window.location.href);
    url.searchParams.set("waited", waited);
    detail.dataset.loaded = "true";

    const html = await this.fetchHtml(url);
    if (html === null) {
      content.textContent = detail.dataset.error;
      delete detail.dataset.loaded;
      return;
    }
    content.innerHTML = html;
    if (content.querySelector("[data-waiting]") && detail.isConnected && !detail.hidden) {
      setTimeout(() => this.load(detail, waited + POLL_MS / 1000), POLL_MS);
    } else if (!content.querySelector(".parts-detail__body")) {
      delete detail.dataset.loaded;
    }
  }

  async verify(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const holder = button.parentElement;
    button.classList.add("is-loading");
    holder.querySelectorAll(".parts-detail__error").forEach((error) => error.remove());

    const html = await this.fetchHtml(button.href);
    if (html === null) {
      button.classList.remove("is-loading");
      const error = document.createElement("span");
      error.className = "parts-detail__error";
      error.textContent = button.closest("tr.parts-detail").dataset.verifyError;
      holder.append(error);
      return;
    }
    holder.innerHTML = html;
  }

  async fetchHtml(url) {
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } });
      return response.ok && !response.redirected ? await response.text() : null;
    } catch (error) {
      return null;
    }
  }

  restore(section) {
    const saved = this.saved[section.dataset.section];
    delete this.saved[section.dataset.section];
    if (!saved) return this.paginate(section, 0);

    saved.open.forEach(({ id, html, loaded }) => {
      const row = section.querySelector(`tr.parts-row[data-detail="${id}"]`);
      if (!row) return;

      const detail = this.detailOf(row);
      detail.querySelector(".parts-detail__content").innerHTML = html;
      if (loaded && !detail.querySelector("[data-waiting]")) detail.dataset.loaded = loaded;
      this.expand(row, true);
    });
    this.paginate(section, saved.page);
  }

  reveal(event) {
    const reveal = event.currentTarget.closest("tbody");
    reveal.nextElementSibling.hidden = false;
    reveal.remove();
  }

  previous(event) {
    this.turn(event, -1);
  }

  next(event) {
    this.turn(event, 1);
  }

  turn(event, step) {
    const section = event.currentTarget.closest("tbody");
    this.paginate(section, Number(section.dataset.page || 0) + step);
  }

  arrange(section) {
    const combination = this.chipTargets.filter((chip) => chip.getAttribute("aria-pressed") === "true").map((chip) => chip.dataset.key).join(",");
    const order = JSON.parse(section.dataset.orders)[combination];
    const rows = new Map(Array.from(section.querySelectorAll("tr.parts-row")).map((row) => [Number(row.dataset.position), row]));
    const pager = section.querySelector(".parts-table__pager");

    order.forEach((position, place) => {
      const row = rows.get(position);
      row.classList.toggle("parts-row--continued", place > 0 && rows.get(order[place - 1]).dataset.yard === row.dataset.yard);
      section.insertBefore(row, pager);
      section.insertBefore(this.detailOf(row), pager);
    });
  }

  paginate(section, page) {
    const rows = Array.from(section.querySelectorAll("tr.parts-row"));
    const yards = Array.from(new Set(rows.map((row) => row.dataset.yard)));
    const pages = Math.max(Math.ceil(yards.length / PAGE_SIZE), 1);
    const current = Math.min(Math.max(page, 0), pages - 1);
    const pager = section.querySelector(".parts-table__pager");
    const [previous, next] = pager.querySelectorAll("button");

    section.dataset.page = current;
    rows.forEach((row) => {
      const onPage = Math.floor(yards.indexOf(row.dataset.yard) / PAGE_SIZE) === current;
      row.hidden = !onPage;
      this.detailOf(row).hidden = !onPage || !row.classList.contains("parts-row--open");
    });
    pager.hidden = pages === 1;
    previous.disabled = current === 0;
    next.disabled = current === pages - 1;
    pager.querySelector(".parts-table__position").textContent = `${current + 1} / ${pages}`;
  }

  detailOf(row) {
    return row.parentElement.querySelector(`[id="${row.dataset.detail}-row"]`);
  }
}
